import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, ShieldCheck, CaretDown, MagnifyingGlass, Check, X as XIcon, Clock, Warning } from '@phosphor-icons/react';

const STATUS_COLORS = {
  active: 'bg-emerald-900/30 text-emerald-400 border-emerald-700/40',
  pending_verification: 'bg-amber-900/30 text-amber-400 border-amber-700/40',
  suspended: 'bg-red-900/30 text-red-400 border-red-700/40',
  restricted: 'bg-orange-900/30 text-orange-400 border-orange-700/40',
  deactivated: 'bg-zinc-700/40 text-zinc-400 border-zinc-600',
};

const KYC_COLORS = {
  not_started: 'bg-zinc-700/40 text-zinc-400 border-zinc-600',
  pending: 'bg-amber-900/30 text-amber-400 border-amber-700/40',
  under_review: 'bg-blue-900/30 text-blue-400 border-blue-700/40',
  approved: 'bg-emerald-900/30 text-emerald-400 border-emerald-700/40',
  rejected: 'bg-red-900/30 text-red-400 border-red-700/40',
  suspended: 'bg-red-900/30 text-red-400 border-red-700/40',
};

const ROLE_COLORS = {
  admin: 'bg-purple-900/30 text-purple-400 border-purple-700/40',
  affiliate: 'bg-blue-900/30 text-blue-400 border-blue-700/40',
  advertiser: 'bg-teal-900/30 text-teal-400 border-teal-700/40',
};

export default function AccountManagement() {
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [reason, setReason] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      const [acctRes, statsRes] = await Promise.all([
        api.get(`/accounts/?${params.toString()}`),
        api.get('/accounts/stats'),
      ]);
      setAccounts(acctRes.data || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredAccounts = accounts.filter(a =>
    (!search || a.email?.toLowerCase().includes(search.toLowerCase()) || a.name?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleStatusUpdate = async (email, newStatus) => {
    try {
      await api.put(`/accounts/${encodeURIComponent(email)}/status`, { status: newStatus, reason });
      toast.success(`Account ${newStatus}`);
      setActionModal(null);
      setSelectedAccount(null);
      setReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleKycUpdate = async (email, newKyc) => {
    try {
      await api.put(`/accounts/${encodeURIComponent(email)}/kyc`, { kyc_status: newKyc, notes: reason });
      toast.success(`KYC status updated to ${newKyc}`);
      setActionModal(null);
      setSelectedAccount(null);
      setReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update KYC');
    }
  };

  return (
    <div className="space-y-6" data-testid="account-management-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Account Management</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage users, verification, and compliance</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Users</p>
              <p className="text-2xl font-bold mt-1" data-testid="total-users">{stats.total}</p>
            </CardContent>
          </Card>
          {Object.entries(stats.by_role || {}).map(([role, count]) => (
            <Card key={role} className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">{role}s</p>
                <p className="text-2xl font-bold mt-1">{count}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users size={18} className="text-blue-400" />
            All Accounts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="search-accounts"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 bg-zinc-800 border-zinc-700 text-sm"
              />
            </div>
            <div className="relative w-36">
              <select
                data-testid="filter-role"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="affiliate">Affiliate</option>
                <option value="advertiser">Advertiser</option>
              </select>
              <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
            <div className="relative w-44">
              <select
                data-testid="filter-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending_verification">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="restricted">Restricted</option>
                <option value="deactivated">Deactivated</option>
              </select>
              <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-zinc-500">Loading accounts...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="accounts-table">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 text-left font-medium">User</th>
                    <th className="px-4 py-3 text-left font-medium">Role</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">KYC</th>
                    <th className="px-4 py-3 text-left font-medium">Joined</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((acct, i) => (
                    <tr key={acct.email || i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-zinc-200 font-medium">{acct.name}</p>
                          <p className="text-zinc-500 text-xs">{acct.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${ROLE_COLORS[acct.role] || ROLE_COLORS.affiliate}`}>
                          {acct.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[acct.status] || STATUS_COLORS.active}`}>
                          {(acct.status || 'active').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${KYC_COLORS[acct.kyc_status] || KYC_COLORS.not_started}`}>
                          {(acct.kyc_status || 'not started').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-500 text-xs">
                        {acct.created_at ? new Date(acct.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {acct.role !== 'admin' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`verify-${acct.email}`}
                                onClick={() => { setSelectedAccount(acct); setActionModal('kyc'); }}
                                className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                              >
                                <ShieldCheck size={14} className="mr-1" /> KYC
                              </Button>
                              {acct.status !== 'suspended' ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  data-testid={`suspend-${acct.email}`}
                                  onClick={() => { setSelectedAccount(acct); setActionModal('suspend'); }}
                                  className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                >
                                  <Warning size={14} className="mr-1" /> Suspend
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  data-testid={`activate-${acct.email}`}
                                  onClick={() => handleStatusUpdate(acct.email, 'active')}
                                  className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
                                >
                                  <Check size={14} className="mr-1" /> Activate
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Modal */}
      {actionModal && selectedAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md mx-4" data-testid="action-modal">
            <CardHeader>
              <CardTitle className="text-lg">
                {actionModal === 'kyc' ? 'Update KYC Status' : 'Suspend Account'}
              </CardTitle>
              <p className="text-sm text-zinc-500">{selectedAccount.name} ({selectedAccount.email})</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {actionModal === 'kyc' && (
                <div className="grid grid-cols-2 gap-2">
                  {['approved', 'rejected', 'under_review', 'pending'].map(status => (
                    <Button
                      key={status}
                      data-testid={`kyc-${status}`}
                      variant="outline"
                      onClick={() => handleKycUpdate(selectedAccount.email, status)}
                      className={`text-xs capitalize border-zinc-700 ${
                        status === 'approved' ? 'hover:bg-emerald-900/20 hover:text-emerald-400' :
                        status === 'rejected' ? 'hover:bg-red-900/20 hover:text-red-400' :
                        'hover:bg-zinc-800'
                      }`}
                    >
                      {status === 'approved' && <Check size={14} className="mr-1" />}
                      {status === 'rejected' && <XIcon size={14} className="mr-1" />}
                      {status === 'under_review' && <Clock size={14} className="mr-1" />}
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              )}
              {actionModal === 'suspend' && (
                <>
                  <Input
                    data-testid="suspend-reason"
                    placeholder="Reason for suspension..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  <Button
                    data-testid="confirm-suspend"
                    onClick={() => handleStatusUpdate(selectedAccount.email, 'suspended')}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    Confirm Suspension
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                onClick={() => { setActionModal(null); setSelectedAccount(null); setReason(''); }}
                className="w-full text-zinc-400"
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
